import type { ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui/button';
import { ErrorCallout } from '@/components/ui/error-callout';

interface FallbackProps {
  readonly error: Error;
  readonly onReset: () => void;
  /**
   * `true` (default) shows "Try again" (soft reset). Boundaries whose only
   * reset is a full reload pass `false` so it doesn't duplicate "Reload".
   */
  readonly canRetry?: boolean;
}

/**
 * Render-time fallback UI for the error boundaries. Separate file so the
 * boundary stays export-clean for react-refresh.
 *
 * Shows only contract-safe details — `ApiError.code` / `correlationId` and
 * stable i18n copy. `error.message` is never shown: it may carry PHI.
 */
export function ErrorFallback({
  error,
  onReset,
  canRetry = true,
}: FallbackProps): ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const queryClient = useQueryClient();

  const apiCode = error instanceof ApiError ? error.code : null;
  const correlationId = error instanceof ApiError ? error.correlationId ?? null : null;

  // Pre-labelled string (no `codeLabel`) fits both ids into the one code slot.
  const codeParts = [
    apiCode !== null ? `${t('error.boundary.codeLabel')} ${apiCode}` : null,
    correlationId !== null ? `${t('error.boundary.correlationLabel')} ${correlationId}` : null,
  ].filter((part): part is string => part !== null);
  const code = codeParts.length > 0 ? codeParts.join('  ·  ') : undefined;

  const handleRetry = (): void => {
    queryClient
      .invalidateQueries()
      .catch(() => {
        // `onReset` still runs user intent; invalidation is best-effort.
      });
    onReset();
  };

  const handleReload = (): void => {
    window.location.reload();
  };

  const handleSignOut = (): void => {
    auth
      .signOut()
      .finally(() => {
        onReset();
      })
      .catch(() => {
        // Reset still attempted in `finally`; swallow remaining rejection.
      });
  };

  return (
    <main className="mx-auto flex min-h-screen max-w-xl flex-col justify-center p-8">
      <ErrorCallout
        role="alert"
        headingAs="h2"
        heading={t('error.boundary.heading')}
        message={t('error.boundary.message')}
        {...(code !== undefined ? { code } : {})}
        actions={
          <>
            {canRetry && (
              <Button onClick={handleRetry}>{t('error.boundary.retry')}</Button>
            )}
            <Button variant={canRetry ? 'outline' : 'default'} onClick={handleReload}>
              {t('error.boundary.reload')}
            </Button>
            {auth.status === 'authenticated' && (
              <Button variant="ghost" onClick={handleSignOut}>
                {t('error.boundary.signOut')}
              </Button>
            )}
          </>
        }
      />
    </main>
  );
}
